import React, { useState, useEffect } from "react"
import { Animated, Platform, Dimensions, View, StyleSheet } from "react-native"
import { IconButton } from "react-native-paper"
import { theme } from "../../theme/theme"
import TextM from "../text/TextM"

function ToastNotification({
    message,
    visible,
    onDismiss,
    duration = 0,
    type = "info",
    position = "bottom",
}) {
    const [fadeAnim] = useState(new Animated.Value(0))
    const [translateY] = useState(new Animated.Value(-100))
    const [scaleAnim] = useState(new Animated.Value(0.8))

    const screenHeight = Dimensions.get("window").height
    const screenWidth = Dimensions.get("window").width

    const width = screenWidth > 800 ? 800 : screenWidth

    useEffect(() => {
        if (visible) {
            // Set initial position based on position prop
            const initialTranslateY = getInitialTranslateY()
            translateY.setValue(initialTranslateY)

            // Slide in and fade in with scale animation
            Animated.parallel([
                Animated.timing(fadeAnim, {
                    toValue: 1,
                    duration: 300,
                    useNativeDriver: true,
                }),
                Animated.timing(translateY, {
                    toValue: 0,
                    duration: 300,
                    useNativeDriver: true,
                }),
                Animated.timing(scaleAnim, {
                    toValue: 1,
                    duration: 300,
                    useNativeDriver: true,
                }),
            ]).start()

            // Auto dismiss after duration (only if duration > 0)
            if (duration > 0) {
                const timer = setTimeout(() => {
                    dismissToast()
                }, duration)
                return () => clearTimeout(timer)
            }
        }
    }, [visible])

    const getInitialTranslateY = () => {
        switch (position) {
            case "top":
                return -100
            case "bottom":
                return 100
            case "center":
                return 0
            default:
                return -100
        }
    }

    const getDynamicStyle = () => {
        const commonStyle = {
            position: "absolute",
            borderLeftColor: theme.colors[type] || theme.colors.primary,
            width: width - 32,
            alignSelf: "center",          
        }

        switch (position) {
            case "top":
                return { ...commonStyle, top: Platform.OS === "ios" ? 50 : 30 }
            case "center":
                return { ...commonStyle, top: screenHeight / 2 - 50 }
            default:
                return {
                    ...commonStyle,
                    bottom: Platform.OS === "ios" ? 100 : 80,
                }
        }
    }

    const dismissToast = () => {
        const finalTranslateY = getInitialTranslateY()

        Animated.parallel([
            Animated.timing(fadeAnim, {
                toValue: 0,
                duration: 200,
                useNativeDriver: true,
            }),
            Animated.timing(translateY, {
                toValue: finalTranslateY,
                duration: 200,
                useNativeDriver: true,
            }),
            Animated.timing(scaleAnim, {
                toValue: 0.8,
                duration: 200,
                useNativeDriver: true,
            }),
        ]).start(() => {
            onDismiss?.()
        })
    }

    if (!visible) return null

    const getIconName = () => {
        switch (type) {
            case "success":
                return "check"
            case "error":
                return "exclamation-triangle"
            case "warning":
                return "exclamation-triangle"
            default:
                return "info"
        }
    }

    const getIconColor = () => {
        switch (type) {
            case "success":
                return theme.colors.success
            case "error":
                return theme.colors.error
            case "warning":
                return theme.colors.warning
            default:
                return theme.colors.primary
        }
    }

    return (
        <Animated.View
            style={[
                styles.container,
                getDynamicStyle(),
                {
                    opacity: fadeAnim,
                    transform: [{ translateY }, { scale: scaleAnim }],
                },
            ]}
        >
            <View
                style={{
                    flexDirection: "row",
                    alignItems: "center",
                    gap: 4,
                    flex: 1,
                }}
            >
                <IconButton
                    icon={getIconName()}
                    iconColor={getIconColor()}
                    size={20}
                    style={{ margin: 0, marginRight: 8 }}
                />
                <TextM
                    style={{
                        color: theme.colors.black,
                        fontSize: 14,
                        lineHeight: 20,
                    }}
                    wrap
                    numberOfLines={3}
                >
                    {message}
                </TextM>
            </View>
            <IconButton
                icon="times"
                iconColor={theme.colors.light}
                size={24}
                onPress={dismissToast}
                style={{ margin: 0 }}
            />
        </Animated.View>
    )
}

const styles = StyleSheet.create({
    container: {
        backgroundColor: theme.colors.white,
        borderRadius: theme.roundness,
        paddingHorizontal: 16,
        paddingVertical: 12,
        flexDirection: "row",
        alignItems: "center",
        justifyContent: "space-between",
        shadowColor: theme.colors.black,
        shadowOffset: { width: 0, height: 2 },
        shadowOpacity: 0.15,
        shadowRadius: 8,
        elevation: 8,
        borderLeftWidth: 4,
        minHeight: 56,
        gap: 10,
        alignSelf: "center",
    },
    mainContent: {
        flexDirection: "row",
        alignItems: "center",
        gap: 10,
        flex: 1,
    },
})

export default ToastNotification
